import { css } from 'aphrodite';
import * as React from 'react';
import { useRef, useState } from 'react';
import { IUser } from '../../../extViewmodels';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { getAvatarProps } from '../../../models/AvatarUtils';
import { getDisplayName } from '../../../models/NameUtils';
import { useUserAutocompleteQuery } from '../../../queries/User/useUserAutocompleteQuery';
import {
	Autocomplete,
	AutocompleteClear,
	AutocompleteContent,
	AutocompleteControl,
	AutocompleteEmpty,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
} from '../../components-v2/Autocomplete';
import { Avatar } from '../../components-v2/Avatar';
import { styleSheet } from './styles';

interface IProps {
	ariaLabel: string;
	id?: string;
	/** Set `value` to the user passed here; the input shows `value` whenever it is not being typed in. */
	onChange: (user: IUser | null) => void;
	placeholder: string;
	value: IUser | null;
}

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_LIMIT = 5;

function getEmptyMessage(fragment: string, isError: boolean, isFetching: boolean): string | null {
	if (fragment.length === 0) {
		return null;
	}
	if (isError) {
		return 'Unable to search users';
	}
	return isFetching ? 'Searching...' : 'No users found';
}

function UserRow({ user }: { user: IUser }) {
	const name = getDisplayName(user);
	// getDisplayName falls back to the email for users without a name; don't print it twice.
	const hasDistinctEmail = Boolean(user.email) && user.email !== name;
	return (
		<>
			<Avatar {...getAvatarProps(user)} altText='' smallText={true} styles={[styleSheet.avatar]} />
			<span className={css(styleSheet.text)}>
				<span className={css(styleSheet.name)}>{name}</span>
				{hasDistinctEmail ? <span className={css(styleSheet.email)}>{user.email}</span> : null}
			</span>
		</>
	);
}

export function UserAutocomplete({ ariaLabel, id, onChange, placeholder, value }: IProps) {
	// null while the input shows the selected user rather than text the user typed.
	const [typedText, setTypedText] = useState<string | null>(null);
	// Names repeat across users, so a pick is resolved from the highlighted item, not the input text.
	const highlightedUserRef = useRef<IUser | null>(null);
	const fragment = useDebounceValue(typedText?.trim() ?? '', SEARCH_DEBOUNCE_MS);

	const { data, isFetching, isError } = useUserAutocompleteQuery({
		enabled: fragment.length > 0,
		enforcePageSize: true,
		fragment,
		pageSize: RESULTS_LIMIT,
	});
	const users = fragment.length > 0 ? (data?.values ?? []) : [];

	const handleValueChange = (text: string, reason: string) => {
		if (reason === 'input-change') {
			setTypedText(text);
			return;
		}
		setTypedText(null);
		if (reason === 'item-press') {
			const picked = highlightedUserRef.current ?? users.find(user => getDisplayName(user) === text);
			if (picked) {
				onChange(picked);
			}
		} else if (reason === 'clear-press' && value) {
			onChange(null);
		}
	};

	const handleOpenChange = (open: boolean, reason: string) => {
		// Base UI closes the popup when the text is emptied; keep the empty text so a delete isn't undone mid-keystroke.
		if (!open && reason !== 'input-clear') {
			setTypedText(null);
		}
	};

	return (
		<Autocomplete
			filter={null}
			items={users}
			itemToStringValue={getDisplayName}
			onItemHighlighted={user => {
				highlightedUserRef.current = user ?? null;
			}}
			onOpenChange={(open, details) => handleOpenChange(open, details.reason)}
			onValueChange={(text, details) => handleValueChange(text, details.reason)}
			value={typedText ?? getDisplayName(value)}
		>
			<AutocompleteControl>
				<AutocompleteInput aria-label={ariaLabel} id={id} onBlur={() => setTypedText(null)} placeholder={placeholder} />
				<AutocompleteClear aria-label='Clear user' />
			</AutocompleteControl>
			<AutocompleteContent styles={[styleSheet.popup]}>
				<AutocompleteEmpty>{getEmptyMessage(fragment, isError, isFetching)}</AutocompleteEmpty>
				<AutocompleteList>
					{users.map(user => (
						<AutocompleteItem key={user.id} styles={[styleSheet.item]} value={user}>
							<UserRow user={user} />
						</AutocompleteItem>
					))}
				</AutocompleteList>
			</AutocompleteContent>
		</Autocomplete>
	);
}
